import { useState } from "react";
import { FormHelperText } from "@mui/material";
import FormItem, { getErrorMessage } from "@components/FormItem/FormItem";
import type { Option } from "@components/FormItem/FormItemFieldRenderer";
import { t } from "i18next";
import { useFormContext, type FieldValues, type Path, type PathValue } from "react-hook-form";

const DEFAULT_LEFT_OPTION: Option = { label: "Yes", value: "true" };
const DEFAULT_RIGHT_OPTION: Option = { label: "N/A", value: "false" };

interface ConditionalTextboxProps<
  T extends FieldValues,
  RadioFieldName extends Path<T>,
  TextboxFieldName extends Path<T>,
> {
  label: string;
  textboxFieldName: TextboxFieldName;
  radioFieldName: RadioFieldName;
  onFieldChange?: (
    formFieldName: RadioFieldName | TextboxFieldName,
    value: PathValue<T, RadioFieldName | TextboxFieldName>,
  ) => void;
  textboxDisabled?: boolean;
  textboxMaxChars?: number;
  leftOption?: Option;
  rightOption?: Option;
  testIdPrefix?: string;
  layoutClassNames?: {
    container?: string;
    grid?: string;
    leftColumn?: string;
    textboxColumn?: string;
    rightColumn?: string;
  };
}

const ConditionalTextbox = <
  T extends FieldValues,
  RadioFieldName extends Path<T>,
  TextboxFieldName extends Path<T>,
>({
  label,
  textboxFieldName,
  radioFieldName,
  onFieldChange,
  textboxDisabled,
  textboxMaxChars = 100,
  leftOption = DEFAULT_LEFT_OPTION,
  rightOption = DEFAULT_RIGHT_OPTION,
  testIdPrefix = "form",
  layoutClassNames,
}: ConditionalTextboxProps<T, RadioFieldName, TextboxFieldName>) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext<T>();

  const [maxLengthAttempted, setMaxLengthAttempted] = useState(false);

  const radioValue = watch(radioFieldName);

  const radioError = getErrorMessage(errors as Record<string, unknown>, String(radioFieldName));
  const textboxError = getErrorMessage(errors as Record<string, unknown>, String(textboxFieldName));

  const shouldValidateTextbox =
    radioValue !== "" && String(radioValue) === String(leftOption.value);

  const maxLengthError = maxLengthAttempted
    ? t("form-validation.max-length", { label, max: textboxMaxChars })
    : "";

  const rowError = shouldValidateTextbox ? textboxError || maxLengthError : radioError;

  const handleFieldChange = <K extends RadioFieldName | TextboxFieldName>(
    fieldName: K,
    value: PathValue<T, K>,
  ) => {
    onFieldChange?.(fieldName, value as PathValue<T, RadioFieldName | TextboxFieldName>);
  };

  const handleRadioChange = (fieldName: RadioFieldName, value: PathValue<T, RadioFieldName>) => {
    if (String(value) !== String(leftOption.value)) {
      setMaxLengthAttempted(false);
    }

    handleFieldChange(fieldName, value);
  };

  const handleTextboxChange = (
    fieldName: TextboxFieldName,
    value: PathValue<T, TextboxFieldName>,
  ) => {
    if (typeof value === "string" && value.length < textboxMaxChars) {
      setMaxLengthAttempted(false);
    }

    handleFieldChange(fieldName, value);
  };

  const handleMaxCharAttempt = () => {
    if (shouldValidateTextbox) {
      setMaxLengthAttempted(true);
    }
  };

  const handleTextboxBlur = () => {
    setMaxLengthAttempted(false);
  };

  const {
    container = "col-span-full",
    grid = "grid grid-cols-1 lg:grid-cols-8 xl:grid-cols-12 lg:gap-x-6",
    leftColumn = "",
    textboxColumn = "lg:col-span-5 lg:ml-2 xl:ml-2",
    rightColumn = "lg:col-span-6",
  } = layoutClassNames ?? {};

  return (
    <div className={container}>
      <p className="text-sm font-medium text-primary-black mb-2">{label}</p>
      <div className={grid}>
        <div className={leftColumn}>
          <FormItem<T, RadioFieldName>
            testId={`${testIdPrefix}-${String(radioFieldName)}-input-left`}
            name={radioFieldName}
            type="radio"
            onFieldChange={handleRadioChange}
            options={[leftOption]}
            showErrorMsg={false}
          />
        </div>
        <div className={textboxColumn} onBlurCapture={handleTextboxBlur}>
          <FormItem<T, TextboxFieldName>
            testId={`${testIdPrefix}-${String(textboxFieldName)}-input-text`}
            name={textboxFieldName}
            type="text"
            onFieldChange={handleTextboxChange}
            disabled={textboxDisabled}
            maxChars={textboxMaxChars}
            onMaxCharAttempt={handleMaxCharAttempt}
            showErrorMsg={false}
          />
        </div>
        <div className={rightColumn}>
          <FormItem<T, RadioFieldName>
            testId={`${testIdPrefix}-${String(radioFieldName)}-input-right`}
            name={radioFieldName}
            type="radio"
            onFieldChange={handleRadioChange}
            options={[rightOption]}
            showErrorMsg={false}
          />
        </div>
      </div>
      {rowError && (
        <FormHelperText sx={{ color: "error.main", m: 0, fontWeight: 500 }}>
          {rowError}
        </FormHelperText>
      )}
    </div>
  );
};

export type { ConditionalTextboxProps };
export default ConditionalTextbox;
